import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  useTheme,
  alpha
} from '@mui/material';
import {
  Security,
  Gavel,
  VerifiedUser,
  Policy,
  DataUsage,
  Shield
} from '@mui/icons-material';
import ValidadoLogo from '../components/ValidadoLogo';
import usePageTitle from '../hooks/usePageTitle';

const CompliancePage: React.FC = () => {
  const theme = useTheme();
  usePageTitle('Legal Compliance Statement');

  const compliancePrinciples = [
    {
      icon: <Gavel sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: 'Legal Compliance Framework',
      description: 'Valida.do operates under strict legal compliance standards, adhering to international data protection laws and background verification regulations.'
    },
    {
      icon: <Security sx={{ fontSize: 40, color: theme.palette.success.main }} />,
      title: 'ISO 27001 Standards',
      description: 'We implement comprehensive security policies following ISO 27001 standards for information security management systems.'
    },
    {
      icon: <VerifiedUser sx={{ fontSize: 40, color: theme.palette.info.main }} />,
      title: 'Data Subject Rights',
      description: 'Full compliance with GDPR, CCPA, and local data protection regulations ensuring data subject rights are protected.'
    },
    {
      icon: <Shield sx={{ fontSize: 40, color: theme.palette.warning.main }} />,
      title: 'Secure Processing',
      description: 'All background verification data is processed using industry-leading security measures and encryption protocols.'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <ValidadoLogo width={200} height={80} />
          <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
            Legal Compliance Statement
          </Typography>
          <Typography variant="h6" color="text.secondary" maxWidth="800px" mx="auto">
            Valida.do's commitment to legal compliance, data protection, and ethical background verification practices
          </Typography>
        </Box>

        {/* General Statement */}
        <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Policy color="primary" sx={{ fontSize: 32 }} />
            General Statement of Legal Compliance
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Valida.do is a professional background verification platform incorporated under applicable laws and 
            providing services globally. Our platform serves businesses across multiple jurisdictions including 
            North America, Latin America, and Europe. To ensure compliance to the greatest extent practicable, 
            Valida.do adheres to strict ISO 27001 standards and implements comprehensive policies regarding 
            safety and best practices during the collection and processing of client and data subject information.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Any differences between Valida.do's policies and local data processing and protection laws within 
            countries where we provide services are accounted for and reflected in our service agreements. 
            As a global platform, we monitor updates regarding data subject rights and employment verification 
            laws in all jurisdictions to ensure safe information exchange and compliance with applicable regulations.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Valida.do does not engage in secondary markets or accumulate client and data subject information 
            for sale to third parties. Client data is used exclusively for performing background checks and 
            identity verification services. All information is securely processed and deleted according to 
            industry best practices and contractual agreements.
          </Typography>
        </Paper>

        {/* Compliance Principles */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
            Our Compliance Principles
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {compliancePrinciples.map((principle, index) => (
              <Card
                key={index}
                elevation={2}
                sx={{
                  flex: { xs: '1 1 100%', md: '1 1 45%' },
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    {principle.icon}
                    <Typography variant="h6" fontWeight="bold">
                      {principle.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {principle.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Data Acquisition and Use */}
        <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DataUsage color="primary" sx={{ fontSize: 32 }} />
            Data Acquisition and Use
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Valida.do's background verification services utilize information from publicly available sources 
            and authorized databases. The information we access is from legitimate sources including government 
            databases, public records, and verified institutional sources. Our data acquisition practices are 
            transparent and compliant with applicable data protection regulations.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            It is the responsibility of our clients to obtain proper consent from data subjects before 
            requesting background verification services. Valida.do requires as a contractual condition 
            that clients warrant data subject consent has been obtained and that subjects are aware of 
            the purpose for which their information will be used.
          </Typography>
        </Paper>

        {/* Data Processing */}
        <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Data Processing and Security
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            To safeguard fairness and integrity in our verification processes, we limit data processing 
            to only what is necessary to fulfill the stated verification purpose. We process information 
            only to the extent authorized by clients and data subjects, employing both automated and 
            manual review processes.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            All automated processes include human oversight, particularly in cases where verification 
            results may have legal or significant effects. We implement reasonable steps to confirm 
            the accuracy and relevance of information within legal and regulatory contexts.
          </Typography>
        </Paper>

        {/* Legal Responsibilities */}
        <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Valida.do's Legal Responsibilities
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            As a background verification service provider, Valida.do operates in compliance with applicable 
            consumer reporting laws, data protection regulations, and employment verification standards. 
            Our responsibilities include:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Data Protection Compliance"
                secondary="Ensuring client and subject data is protected according to applicable privacy laws"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Accuracy and Completeness"
                secondary="Maintaining high standards for information accuracy and verification completeness"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Consent Management"
                secondary="Ensuring proper consent mechanisms are in place for all verification processes"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Secure Data Handling"
                secondary="Implementing industry-standard security measures for all data processing activities"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Regulatory Compliance"
                secondary="Adhering to employment verification laws and consumer protection regulations"
              />
            </ListItem>
          </List>
        </Paper>

        {/* Contact Information */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 4, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Questions About Our Compliance?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            For questions regarding our legal compliance, data protection practices, or verification procedures, 
            please contact our compliance team.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email: compliance@valida.do | Phone: +1 (555) 123-4567
          </Typography>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 6, pt: 4, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Valida.do. All rights reserved. | Last updated: January 2025
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default CompliancePage;